import Avatar from 'react-avatar';
import {
  RiArrowRightSLine,
  RiArrowUpDownLine,
  RiBankLine,
  RiGlobalLine,
  RiMailLine,
  RiMapPinLine,
  RiPhoneLine,
  RiUserAddLine,
  RiWhatsappLine,
} from 'react-icons/ri';
import { BsSignal } from 'react-icons/bs';

import { rsdaLogo } from '../assets';
import BottomSheet from '../components/BottomSheet';
import BkArenaModal from './modals/BkArenaModal';
import { truncateString } from '../utils';

// api
const API_URL = String(process.env.REACT_APP_BASE_API_URL || '').replace(/\/+$/, '');

// brand
const NAVY = '#2B4B8E';
const SKY = '#3F7CC4';
const INK = '#151515';
const MUTED = '#6B7280';
const CREAM = '#F7F5EE';
const PAGE = '#EEECE5';
const TILE = '#E8EEF8';
const LINE = '#E6E8EC';
const BRAND_NAME = 'RSDA';

// type
const SERIF = "'Cormorant Garamond', 'EB Garamond', Garamond, Georgia, 'Times New Roman', serif";
const SANS = "'Jost', 'GT Walsheim', 'GT-Walsheim-Regular', sans-serif";
const serif = { fontFamily: SERIF, fontWeight: 600 };
const regular = { fontFamily: SANS, fontWeight: 400 };
const medium = { fontFamily: SANS, fontWeight: 500 };
const bold = { fontFamily: SANS, fontWeight: 600 };

// ---------- sanitizers (all values come from a remote API) ----------

const asText = value => (typeof value === 'string' ? value.trim() : '');

// Positions may be stored with markup (see other profiles); render them as plain text.
const asPlainText = value =>
  asText(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Only http(s) URLs may be used as clickable links. Schemeless hosts get https://.
const safeHttpUrl = value => {
  const text = asText(value);
  if (!text || /\s/.test(text) || [...text].some(ch => ch.charCodeAt(0) < 32)) return '';
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
};

// Only http(s) or inline image data may be used as an avatar source.
const safeImageUrl = value => {
  const text = asText(value);
  if (!text) return '';
  if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,[a-z0-9+/=]+$/i.test(text)) return text;
  return safeHttpUrl(text);
};

const safeTel = value => {
  const digits = asText(value).replace(/[^0-9+]/g, '');
  return digits.length >= 3 ? `tel:${digits}` : '';
};

const safeMailto = value => {
  const text = asText(value);
  return /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(text) ? `mailto:${encodeURIComponent(text)}` : '';
};

const safeMap = value => {
  const text = asText(value);
  return text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}` : '';
};

const asList = value => (Array.isArray(value) ? value.filter(item => item && asText(item.value)) : []);

// "JEAN-PIERRE D'ARC" / "jean-pierre d'arc" -> "Jean-Pierre D'Arc"; mixed case is left as typed.
const properCase = value => {
  const text = asText(value).replace(/\s+/g, ' ');
  if (!text) return '';
  const isShouting = text === text.toUpperCase();
  const isWhispering = text === text.toLowerCase();
  if (!isShouting && !isWhispering) return text;
  return text
    .toLowerCase()
    .replace(/(^|[\s\-'’])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase())
    .replace(/(\S\s)(De|Du|Des|La|Le|Les|Van|Von|Der|Den|Ter|Ten|Da|Di|Del|Della)(?=\s\S)/g, (m, pre, w) => pre + w.toLowerCase());
};

// Sentence case for the role line (the CSS renders it uppercase; this keeps the data tidy).
const sentenceCase = value => {
  const text = asPlainText(value);
  if (!text) return '';
  const isShouting = text === text.toUpperCase();
  return isShouting ? text.charAt(0) + text.slice(1).toLowerCase() : text;
};

// Display formatting for phone numbers. French numbers get the national/international
// grouping, North American numbers 3-3-4, other international numbers are grouped after
// the country code. Anything unrecognisable is shown as typed.
const ONE_DIGIT_CC = ['1', '7'];
const TWO_DIGIT_CC = [
  '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84',
  '86', '90', '91', '92', '93', '94', '95', '98',
];

const groupDigits = text => {
  const groups = [];
  for (let i = text.length; i > 0; i -= 3) groups.unshift(text.slice(Math.max(0, i - 3), i));
  if (groups.length > 1 && groups[0].length === 1) groups.splice(0, 2, groups[0] + groups[1]);
  return groups.join(' ');
};

const formatPhone = value => {
  const raw = asText(value);
  let digits = raw.replace(/\(\s*0\s*\)/, '').replace(/[^0-9+]/g, '');
  if (digits.startsWith('00')) digits = `+${digits.slice(2)}`;
  const plus = digits.startsWith('+');
  digits = digits.replace(/\+/g, '');
  if (!digits) return raw;

  const pairs = text => (text.match(/.{1,2}/g) || []).join(' ');

  // 01 47 03 13 13
  if (!plus && digits.length === 10 && digits.startsWith('0')) return pairs(digits);
  if (!plus) return digits.length > 6 ? groupDigits(digits) : raw;

  const ccLength = ONE_DIGIT_CC.includes(digits.charAt(0)) ? 1 : TWO_DIGIT_CC.includes(digits.slice(0, 2)) ? 2 : 3;
  const cc = digits.slice(0, ccLength);
  const rest = digits.slice(ccLength);
  if (!rest) return raw;

  // +33 1 47 03 13 13
  if (cc === '33' && rest.length === 9) return `+33 ${rest.charAt(0)} ${pairs(rest.slice(1))}`;
  // +1 415 555 2671
  if (cc === '1' && rest.length === 10) return `+1 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  // +250 788 123 456
  return `+${cc} ${groupDigits(rest)}`;
};

// Best-effort E.164 ("+33612345678"). French national numbers get +33; numbers that already
// carry a country code keep it; anything shorter is rejected (messaging links need a country code).
const toE164 = value => {
  let digits = asText(value).replace(/\(\s*0\s*\)/, '').replace(/[^0-9+]/g, '');
  if (digits.startsWith('00')) digits = `+${digits.slice(2)}`;
  const plus = digits.startsWith('+');
  digits = digits.replace(/\+/g, '');
  if (plus) return digits.length >= 8 ? `+${digits}` : '';
  if (digits.length === 10 && digits.startsWith('0')) return `+33${digits.slice(1)}`;
  return digits.length >= 11 ? `+${digits}` : '';
};

const safeWhatsapp = value => {
  const e164 = toE164(value);
  return e164 ? `https://wa.me/${e164.slice(1)}` : '';
};

const safeSignal = value => {
  const e164 = toE164(value);
  return e164 ? `https://signal.me/#p/${e164}` : '';
};

// ---------- inline tags ----------
//
// The backend only accepts the fixed vCard kinds (PREF, WORK, HOME, CELL, FAX, OTHER) as a
// field type, so extra information is typed straight into the value box as a bracketed list:
//   +250789395643 ["call", "whatsapp"]
//   contact@rsda.eu ["work"]
// The list is tolerant: single/double/no quotes, trailing commas, any spacing. It is removed
// from what is displayed and linked, and its tokens feed the channel detection below.
const splitTags = raw => {
  const text = asText(raw);
  const match = text.match(/\[([^\]]*)\]/);
  if (!match) return { value: text, tags: [] };
  const tags = match[1]
    .split(/[,\s]+/)
    .map(t => t.replace(/^['"`]+|['"`]+$/g, '').trim().toLowerCase())
    .filter(Boolean);
  const value = text.replace(match[0], '').replace(/\s+/g, ' ').trim();
  return { value, tags };
};

// ---------- phone channels ----------
//
// The API only sends { type, value } per phone. To say how a number can be reached, mark it
// in any of these ways (all are understood here):
//   value:    "+250789395643 [\"call\", \"whatsapp\", \"signal\"]"   (see splitTags above)
//   type:     "CELL;WHATSAPP;SIGNAL"   (tokens split on ; , / | or spaces, case-insensitive)
//   channels: ["call", "whatsapp", "signal"]
//   flags:    whatsapp: true, signal: true, call: true   (is_whatsapp / is_signal also work)
// A number with no channel marker at all is treated as a plain call number.

const VCARD_KINDS = ['pref', 'main', 'work', 'home', 'cell', 'mobile', 'fax', 'voice', 'other'];
const CALL_TOKENS = ['call', 'tel', 'phone', 'voice', 'appel'];
const KIND_LABELS = {
  cell: 'Mobile',
  mobile: 'Mobile',
  work: 'Bureau',
  home: 'Domicile',
  pref: 'Principal',
  main: 'Principal',
  fax: 'Fax',
};

const phoneChannels = (item, inlineTags = []) => {
  const tokens = [...inlineTags];
  if (Array.isArray(item?.channels)) tokens.push(...item.channels.map(asText));
  ['whatsapp', 'signal', 'call'].forEach(key => {
    if (item?.[key] === true || item?.[`is_${key}`] === true) tokens.push(key);
  });
  tokens.push(...asText(item?.type).split(/[;,/|\s]+/));
  const lower = tokens.map(t => t.toLowerCase()).filter(Boolean);

  const isWhatsapp = t => /^whats?app$/.test(t);
  const isCall = t => CALL_TOKENS.includes(t);
  const whatsapp = lower.some(isWhatsapp);
  const signal = lower.includes('signal');
  const kind = lower.find(t => VCARD_KINDS.includes(t));

  // An inline list that names channels is authoritative: ["whatsapp"] means WhatsApp only,
  // even though the backend forces a vCard kind such as CELL on the field.
  const inline = inlineTags.map(t => t.toLowerCase());
  const inlineNamesChannels = inline.some(t => isWhatsapp(t) || t === 'signal' || isCall(t));
  const call = inlineNamesChannels
    ? inline.some(isCall)
    : lower.some(isCall) || Boolean(kind) || (!whatsapp && !signal);

  let label = 'Téléphone';
  if (kind && KIND_LABELS[kind]) label = KIND_LABELS[kind];
  else if (!call && whatsapp && !signal) label = 'WhatsApp';
  else if (!call && signal && !whatsapp) label = 'Signal';

  return { whatsapp, signal, call, label };
};

const isExternal = href => /^https?:/i.test(href);

// ---------- pieces ----------

// Renders a link only when the href passed sanitizing; otherwise a plain block.
const SafeLink = ({ href, className, children, ...rest }) => {
  if (!href) {
    return <div className={className}>{children}</div>;
  }
  const external = isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <a href={href} className={className} {...external} {...rest}>
      {children}
    </a>
  );
};

// one contact card; `trailing` (optional) replaces the chevron with action buttons
const InfoCard = ({ href, Icon, label, value, trailing }) => (
  <div className='flex items-center gap-3 bg-white rounded-[18px] border p-[14px] text-left' style={{ borderColor: LINE }}>
    <SafeLink href={href} className='flex items-center gap-4 min-w-0 flex-1'>
      <div
        className='flex-shrink-0 w-[56px] h-[56px] rounded-[14px] flex items-center justify-center'
        style={{ backgroundColor: TILE }}
      >
        <Icon size={24} style={{ color: NAVY }} aria-hidden='true' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-[15px] leading-5' style={{ ...bold, color: INK }}>
          {label}
        </p>
        <p className='text-[15px] leading-6 truncate' style={{ ...regular, color: MUTED }}>
          {value}
        </p>
      </div>
    </SafeLink>
    {trailing
      ? trailing
      : href && <RiArrowRightSLine size={22} className='flex-shrink-0' style={{ color: '#9CA3AF' }} aria-hidden='true' />}
  </div>
);

// square action button for a messaging channel (WhatsApp / Signal)
const ChannelButton = ({ href, label, color, Icon }) => (
  <SafeLink
    href={href}
    aria-label={label}
    title={label}
    className='flex-shrink-0 w-[46px] h-[46px] rounded-[14px] flex items-center justify-center text-white'
    style={{ backgroundColor: color }}
  >
    <Icon size={24} aria-hidden='true' />
  </SafeLink>
);

// section title flanked by hairlines ("——— Coordonnées ———")
const SectionTitle = ({ children }) => (
  <div className='flex items-center gap-4 mb-5'>
    <span className='h-px flex-1' style={{ backgroundColor: '#D9DBE0' }} />
    <h2 className='text-[22px] leading-7 italic' style={{ ...serif, color: INK }}>
      {children}
    </h2>
    <span className='h-px flex-1' style={{ backgroundColor: '#D9DBE0' }} />
  </div>
);

// ---------- page ----------

const RsdaProfile = ({ profile, toggle, modal, setModal, id, isLoading }) => {
  const info = profile?.data?.tag_info;

  const firstName = properCase(info?.fname);
  const lastName = properCase(info?.lname);
  const fullName = [asText(info?.title), firstName, lastName].filter(Boolean).join(' ');
  const position = sentenceCase(info?.position);
  const companyName = asText(info?.company);
  const avatarUrl = safeImageUrl(info?.avatar);
  const vcardUrl = API_URL && id ? `${API_URL}/api/v1/cards/vcard/${encodeURIComponent(String(id))}` : '';

  const phones = asList(info?.phones);
  const emails = asList(info?.emails);
  const websites = asList(info?.websites);
  const addresses = asList(info?.addresses);

  return (
    <>
      {info && (
        <div className='min-h-screen pb-6 py-xl-6 px-l-4' style={{ ...regular, backgroundColor: PAGE }}>
          <div
            className='mx-auto max-w-[440px] bg-white rounded-xl-[36px] p-[14px] pb-6 text-center'
            style={{ boxShadow: '0 20px 60px rgba(43,75,142,0.10)' }}
          >
            {/* Hero: cream field with the RSDA lockup, closed by a navy wave */}
            <div className='relative rounded-[22px] overflow-hidden' style={{ backgroundColor: CREAM }}>
              <div className='flex items-center justify-center px-10 pt-9 pb-[92px]'>
                <img
                  src={rsdaLogo}
                  alt={`${BRAND_NAME} — Société d'Avocats`}
                  className='w-full max-w-[220px] h-auto'
                  draggable='false'
                />
              </div>

              {/* wave: thin cream hairline riding on top of the navy band */}
              <svg
                className='absolute bottom-0 left-0 w-full'
                viewBox='0 0 440 96'
                preserveAspectRatio='none'
                aria-hidden='true'
                style={{ height: 96, display: 'block' }}
              >
                <path d='M0 34 C 120 96, 320 -22, 440 40 L440 96 L0 96 Z' fill={NAVY} />
                <path
                  d='M0 26 C 120 90, 320 -30, 440 32'
                  fill='none'
                  stroke='#C9CDD6'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                />
              </svg>
            </div>

            {/* Avatar straddling the hero edge */}
            <div className='relative -mt-[76px] flex justify-center'>
              <div className='rounded-full bg-white p-[7px]' style={{ boxShadow: '0 8px 24px rgba(21,21,21,0.14)' }}>
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={fullName || 'Photo de profil'}
                    referrerPolicy='no-referrer'
                    className='w-[134px] h-[134px] object-cover object-top rounded-full'
                  />
                ) : (
                  <Avatar
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...bold }}
                    name={`${firstName} ${lastName}`.trim() || BRAND_NAME}
                    round
                    color={NAVY}
                    fgColor='#fff'
                    size={134}
                  />
                )}
              </div>
            </div>

            {/* Names */}
            <div className='px-4 pt-5'>
              <h1 className='text-[26px] leading-8 break-words' style={{ ...serif, color: INK }}>
                {fullName}
              </h1>
              {position && (
                <div className='mt-1 flex items-center justify-center gap-3'>
                  <span className='h-px w-5 flex-shrink-0' style={{ backgroundColor: '#C9CDD6' }} />
                  <p
                    className='text-[11px] leading-4 tracking-[0.18em] uppercase max-w-[260px]'
                    style={{ ...medium, color: SKY }}
                  >
                    {position}
                  </p>
                  <span className='h-px w-5 flex-shrink-0' style={{ backgroundColor: '#C9CDD6' }} />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className='px-2 mt-7 flex items-center gap-4'>
              <button
                type='button'
                onClick={toggle}
                className='flex-1 flex items-center justify-center gap-2 h-[62px] rounded-[14px] border bg-white text-[17px]'
                style={{ ...medium, borderColor: INK, color: INK }}
              >
                <RiArrowUpDownLine size={20} aria-hidden='true' />
                Échanger
              </button>

              {vcardUrl && (
                <a
                  href={vcardUrl}
                  className='flex-1 flex items-center justify-center gap-2 h-[62px] rounded-[14px] text-white text-[17px]'
                  style={{ ...medium, backgroundColor: NAVY, boxShadow: '0 10px 24px rgba(43,75,142,0.30)' }}
                >
                  <RiUserAddLine size={20} aria-hidden='true' />
                  Enregistrer
                </a>
              )}
            </div>

            {/* Contact details */}
            <div className='px-2 mt-9'>
              <SectionTitle>Coordonnées</SectionTitle>

              <div className='space-y-4'>
                {companyName && <InfoCard Icon={RiBankLine} label='Cabinet' value={companyName} />}

                {phones.map((item, index) => {
                  const { value, tags } = splitTags(item.value);
                  const shown = formatPhone(value);
                  const { whatsapp, signal, call, label } = phoneChannels(item, tags);
                  const waHref = whatsapp ? safeWhatsapp(value) : '';
                  const sigHref = signal ? safeSignal(value) : '';
                  const actions = [
                    waHref && (
                      <ChannelButton
                        key='wa'
                        href={waHref}
                        label={`WhatsApp ${shown}`}
                        color='#5DD66C'
                        Icon={RiWhatsappLine}
                      />
                    ),
                    sigHref && (
                      <ChannelButton key='sig' href={sigHref} label={`Signal ${shown}`} color='#3A76F0' Icon={BsSignal} />
                    ),
                  ].filter(Boolean);
                  return (
                    <InfoCard
                      key={`phone-${index}-${value}`}
                      href={call ? safeTel(value) : ''}
                      Icon={RiPhoneLine}
                      label={label}
                      value={shown}
                      trailing={actions.length ? <div className='flex items-center gap-2 flex-shrink-0'>{actions}</div> : null}
                    />
                  );
                })}

                {emails.map((item, index) => {
                  const { value } = splitTags(item.value);
                  return (
                    <InfoCard
                      key={`email-${index}-${value}`}
                      href={safeMailto(value)}
                      Icon={RiMailLine}
                      label='E-mail'
                      value={value}
                    />
                  );
                })}

                {addresses.map((item, index) => {
                  const { value } = splitTags(item.value);
                  return (
                    <InfoCard
                      key={`address-${index}-${value}`}
                      href={safeMap(value)}
                      Icon={RiMapPinLine}
                      label='Adresse'
                      value={value}
                    />
                  );
                })}

                {websites.map((item, index) => {
                  const { value } = splitTags(item.value);
                  return (
                    <InfoCard
                      key={`web-${index}-${value}`}
                      href={safeHttpUrl(value)}
                      Icon={RiGlobalLine}
                      label='Site web'
                      value={truncateString(value, 35)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {info && (
        <BottomSheet isOpen={modal} onChange={toggle} tagInfo={info}>
          <BkArenaModal info={info} setModal={setModal} tagId={profile?.data?.id} />
        </BottomSheet>
      )}

      {!info && !isLoading && (
        <div className='mx-auto max-w-[560px] text-center pt-[45vh] px-6' style={regular}>
          <img src={rsdaLogo} alt={BRAND_NAME} className='mx-auto mb-4 w-[160px] h-auto' />
          <h3 className='text-[15px] leading-6' style={{ ...bold, color: NAVY }}>
            We couldn't find that profile
          </h3>
          <p className='pt-2 text-[12px] leading-5 text-red-900'>
            This might be due to poor network or that profile doesn't exist
          </p>
        </div>
      )}
    </>
  );
};

export default RsdaProfile;
